import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { useSelector } from 'react-redux'

import { ATTENTION_STATUS_BITS } from 'partner360/entities/attentionStatuses'
import { NetworkDebugSource } from 'shared/lib/networkDebug'

import { selectCockpitOffering } from '../../model/selectors'

import { AttentionStatusAcknowledgment } from './components/attentionStatusAcknowledgment'
import { AttentionStatusCompleteness } from './components/attentionStatusCompleteness'
import { AttentionStatusCompletenessOverall } from './components/attentionStatusCompletenessOverall'
import { AttentionStatusFinSobQuality } from './components/attentionStatusFinSobQuality'
import { AttentionStatusH2Review } from './components/attentionStatusH2Review'
import { AttentionStatusInitiativesQuality } from './components/attentionStatusInitiativesQuality'
import { AttentionStatusMdf } from './components/attentionStatusMdf'
import { AttentionStatusPendingActions } from './components/attentionStatusPendingActions'
import { AttentionStatusProgramRisk } from './components/attentionStatusProgramRisk'
import { AttentionStatusQuarterPlan } from './components/attentionStatusQuarterPlan'
import { AttentionStatusSobActuals } from './components/attentionStatusSobActuals'

/* `Overall` counts the status for every programme of the partner, so its detail lists them all
   rather than the one the status was first raised for. */
const AttentionStatusPlanned = () =>
  useSelector(selectCockpitOffering) === ProgramTypes.Overall ? (
    <AttentionStatusCompletenessOverall />
  ) : (
    <AttentionStatusCompleteness />
  )

const renderSection = (bitValue: number) => {
  switch (bitValue) {
    /* The sheet gives the eligibility status the acknowledgment status' content outright: the
       partner regains eligibility once the plan is signed, so the signatures are what both show. */
    case ATTENTION_STATUS_BITS.acknowledgment:
    case ATTENTION_STATUS_BITS.mdfEligibility:
      return <AttentionStatusAcknowledgment />
    case ATTENTION_STATUS_BITS.planned:
      return <AttentionStatusPlanned />
    case ATTENTION_STATUS_BITS.finSobQuality:
      return <AttentionStatusFinSobQuality />
    case ATTENTION_STATUS_BITS.initiativesQuality:
      return <AttentionStatusInitiativesQuality />
    case ATTENTION_STATUS_BITS.mdf:
      return <AttentionStatusMdf />
    case ATTENTION_STATUS_BITS.sobActuals:
      return <AttentionStatusSobActuals />
    case ATTENTION_STATUS_BITS.h2Review:
      return <AttentionStatusH2Review />
    case ATTENTION_STATUS_BITS.programRisk:
      return <AttentionStatusProgramRisk />
    case ATTENTION_STATUS_BITS.quarterPlan:
      return <AttentionStatusQuarterPlan />
    case ATTENTION_STATUS_BITS.pendingActions:
      return <AttentionStatusPendingActions />
    default:
      return null
  }
}

/* The name each section is recorded under in the network data window. */
const SECTION_SOURCE_NAMES: Record<number, string> = {
  [ATTENTION_STATUS_BITS.acknowledgment]: 'Not acknowledged',
  [ATTENTION_STATUS_BITS.mdfEligibility]: 'MDF eligibility',
  [ATTENTION_STATUS_BITS.planned]: 'Not planned',
  [ATTENTION_STATUS_BITS.finSobQuality]: 'Fin & SOB quality',
  [ATTENTION_STATUS_BITS.initiativesQuality]: 'Initiatives quality',
  [ATTENTION_STATUS_BITS.mdf]: 'MDF',
  [ATTENTION_STATUS_BITS.sobActuals]: 'SOB actuals',
  [ATTENTION_STATUS_BITS.h2Review]: 'H2 review',
  [ATTENTION_STATUS_BITS.programRisk]: 'Program risk',
  [ATTENTION_STATUS_BITS.quarterPlan]: 'Quarter plan',
  [ATTENTION_STATUS_BITS.pendingActions]: 'Pending actions',
}

/**
 * The section a status' detail shows, by the status bit it belongs to. The sections are written
 * for one partner's cockpit; the manager cockpit renders them inside `CockpitPartnerScope`.
 */
export const renderAttentionStatusSection = (bitValue: number) => {
  const section = renderSection(bitValue)

  return (
    section && (
      <NetworkDebugSource
        name={`What needs attention › ${SECTION_SOURCE_NAMES[bitValue] ?? String(bitValue)}`}
      >
        {section}
      </NetworkDebugSource>
    )
  )
}
